import { AUTOMAPPING_FOLDER_DEFAULT_NAME } from '@App/app/configs/app.config';
import { LayerHttpService } from '@App/app/engine/services/layer-services/layer-http-service/layer-http.service';
import { Automapping } from '@App/app/entities/files/automapping.model';
import { AUTOMAPPING_IMPORTABLE_LAYER } from '@App/app/entities/layer/enums/layer-types.enum';
import { LayerUiExpandable } from '@App/app/entities/layer/layer-ui.model';
import { ModelsHttpService } from '@App/app/pages/models/services/models-http-service/models-http.service';
import { ConfirmationDialogComponent } from '@App/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { Injectable, Optional } from '@angular/core';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { filter, first, switchMap, take } from 'rxjs/operators';
import {
  AUTOMAPPING_NOT_AVAILABLE,
  FAILED,
  NO_MATCHED_LAYERS,
} from '../../../../configs/toastr-events.config';
import { WARNING_TOASTR_CONFIG } from '../../../../configs/toastr-messages.config';
import { addLayersLocal } from '../../store/actions/layers.actions';
import { LayersService } from '../layers/layers.service';
import { AutomappingUtilsService } from './services/automapping-utils/automapping-utils.service';

@Injectable()
export class AutomappingService {
  private _loading$ = new BehaviorSubject<boolean>(false);
  loading$ = this._loading$.asObservable();

  constructor(
    private _modelHttpService: ModelsHttpService,
    private _layersService: LayersService,
    private _layerHttpService: LayerHttpService,
    private _toastrService: NbToastrService,
    private _automappingUtilsService: AutomappingUtilsService,
    private _dialogService: NbDialogService,
    @Optional() private _dialogRef: NbDialogRef<any>,
    private store: Store,
  ) {}

  setLoading(value: boolean) {
    this._loading$.next(value);
  }

  openDialog(automappingPath: string) {
    this.loading$
      .pipe(
        first(),
        filter((val) => !val),
        switchMap(
          () =>
            this._dialogService.open(ConfirmationDialogComponent, {
              context: {
                title: 'Are you sure you want to import auto-generated cuboids?',
                status: 'info',
              },
            }).onClose,
        ),
        filter((val) => val),
      )
      .subscribe(() => {
        this.importMeshes(automappingPath);
        this._dialogRef?.close();
      });
  }

  importMeshes(path: string) {
    this._loading$.next(true);
    this._modelHttpService
      .getModelAutomapping(path)
      .pipe(take(1))
      .subscribe(
        (result) => {
          if (result.levels && (result.levels as any[])[0]?.type === 'level') {
            this._importRadCentersWithVersion(result as Automapping.Format.RadCentersWithVersion);
          } else if (!Array.isArray(result)) {
            this._importFlatListWithVersion(result as Automapping.Format.FlatListWithVersion);
          } else {
            this._importList(result as Automapping.Format.FlatList);
          }
        },
        () => {
          this._loading$.next(false);
          this._toastrService.show(AUTOMAPPING_NOT_AVAILABLE, FAILED, {
            ...WARNING_TOASTR_CONFIG,
          });
        },
      );
  }

  private _importFlatListWithVersion(result: Automapping.Format.FlatListWithVersion) {
    this._importList(result.meshes, this._automappingUtilsService.getVersion(result.version));
  }

  private _importList(meshes: AUTOMAPPING_IMPORTABLE_LAYER[], version = '') {
    if (meshes.length) {
      const body = this._automappingUtilsService.createFolderData(
        `${AUTOMAPPING_FOLDER_DEFAULT_NAME} ${version}`,
      );
      this._layerHttpService
        .postBatchLayers([body])
        .pipe(take(1))
        .subscribe(([root]) => {
          root.isSaved = true;
          this.store.dispatch(addLayersLocal({ layers: [root] }));
          meshes.forEach((mesh) => (mesh.parentId = root.id));
          this._automappingUtilsService.moveLayersToZeroPoint(meshes);
          this._layersService.importLayers(meshes);
        });
    } else {
      this._loading$.next(false);
      this._toastrService.show(NO_MATCHED_LAYERS, FAILED, WARNING_TOASTR_CONFIG);
    }
  }

  private async _importRadCentersWithVersion(result: Automapping.Format.RadCentersWithVersion) {
    const { levels: pureRadCenters } = result;
    const version = this._automappingUtilsService.getVersion(result.version);
    const body = this._automappingUtilsService.createFolderData(
      `${AUTOMAPPING_FOLDER_DEFAULT_NAME} ${version}`,
    );
    this._layerHttpService
      .postBatchLayers([body])
      .pipe(take(1))
      .subscribe(async ([root]: LayerUiExpandable[]) => {
        const radCenters = await this._automappingUtilsService.createRadCenters(
          pureRadCenters,
          root,
        );
        const sectors = await this._automappingUtilsService.createSectors(
          pureRadCenters,
          radCenters,
        );
        const mountGroups = await this._automappingUtilsService.createMountGroups(
          pureRadCenters,
          sectors,
        );
        const meshes = this._automappingUtilsService.getAllMeshes(pureRadCenters, mountGroups);
        if (meshes.length) {
          this._automappingUtilsService.moveLayersToZeroPoint(meshes);
          this._layersService.importLayers(meshes);
        } else {
          this._loading$.next(false);
          this._toastrService.show(NO_MATCHED_LAYERS, FAILED, {
            ...WARNING_TOASTR_CONFIG,
          });
        }
        const allFolders = [root, ...radCenters, ...sectors, ...mountGroups];
        allFolders.forEach((folder) => (folder.isSaved = true));
        this.store.dispatch(addLayersLocal({ layers: allFolders }));
      });
  }
}
